/**
 * SalesPerformanceCard — Sales team scorecard showing rep rankings by won value,
 * pipeline, win rate, and activities. CEO/Manager view.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { targetAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Trophy, TrendingUp, Target, Users, Activity, Medal, Crown } from 'lucide-react';
import { buildHealthCardParams } from './healthCardParams';
import { HealthFilterScopeRibbon } from './HealthFilterScopeRibbon';

const TABS = [
  { key: 'rankings', label: 'Rankings', icon: Trophy },
  { key: 'pipeline', label: 'Pipeline', icon: Target },
  { key: 'activity', label: 'Activity', icon: Activity },
];

const fmt = (v) => {
  if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v/1e3).toFixed(0)}K`;
  return v?.toFixed?.(0) ?? v;
};

export function SalesPerformanceCard({ filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('rankings');
  const requestParams = buildHealthCardParams(filters);
  const requestParamsKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  useEffect(() => {
    setLoading(true);
    targetAPI.getSalesPerformance(requestParams)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParamsKey]);

  if (loading) return <div className="p-4 space-y-3"><Skeleton className="h-8 w-48" /><Skeleton className="h-32" /></div>;
  if (!data) return <div className="p-4 text-gray-400 text-sm">No data available</div>;

  const { summary, scorecards } = data;
  const sorted = [...(scorecards || [])];

  return (
    <div className="h-full flex flex-col bg-white rounded-xl border overflow-hidden" data-testid="sales-performance-card">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            <h3 className="text-sm font-bold">Sales Performance</h3>
          </div>
          <Badge className="bg-white/20 text-white text-[10px] border-0">{summary?.year}</Badge>
        </div>
        <div className="mt-2"><HealthFilterScopeRibbon filters={filters} /></div>
        <div className="grid grid-cols-4 gap-2 mt-2">
          <div><p className="text-[10px] text-white/70">Team Won</p><p className="text-sm font-bold">{fmt(summary?.total_won_value)}</p></div>
          <div><p className="text-[10px] text-white/70">Pipeline</p><p className="text-sm font-bold">{fmt(summary?.total_pipeline_value)}</p></div>
          <div><p className="text-[10px] text-white/70">Win Rate</p><p className="text-sm font-bold">{summary?.avg_win_rate}%</p></div>
          <div><p className="text-[10px] text-white/70">Reps</p><p className="text-sm font-bold">{summary?.total_reps}</p></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b px-2">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 transition-all ${tab === t.key ? 'border-amber-600 text-amber-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <t.icon className="h-3 w-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-3 min-h-0">
        {tab === 'rankings' && (
          <div className="space-y-1.5">
            {sorted.sort((a,b) => b.won_value - a.won_value).map((rep, i) => (
              <div key={rep.name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-all">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-50 text-orange-600' : 'bg-gray-50 text-gray-400'}`}>
                  {i === 0 ? <Crown className="h-3 w-3" /> : i < 3 ? <Medal className="h-3 w-3" /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{rep.name}</p>
                  <p className="text-[10px] text-gray-400">{rep.won_count} won | WR: {rep.win_rate}%</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-emerald-600">{fmt(rep.won_value)}</p>
                  <p className="text-[10px] text-gray-400">pipe: {fmt(rep.pipeline_value)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === 'pipeline' && (
          <div className="space-y-1.5">
            {sorted.sort((a,b) => b.pipeline_value - a.pipeline_value).map(rep => (
              <div key={rep.name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{rep.name}</p>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                    <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{ width: `${Math.min(100, (rep.pipeline_value / (summary?.total_pipeline_value || 1)) * 100)}%` }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-bold text-blue-600">{fmt(rep.pipeline_value)}</p>
                  <p className="text-[10px] text-gray-400">{rep.pipeline_count} deals</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === 'activity' && (
          <div className="space-y-1.5">
            {sorted.sort((a,b) => b.activities - a.activities).map(rep => (
              <div key={rep.name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{rep.name}</p>
                  <p className="text-[10px] text-gray-400">WR: {rep.win_rate}% | Conv: {rep.conversion_score}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="h-3 w-3 text-violet-500" />
                  <span className="text-xs font-bold text-violet-600">{rep.activities}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
